import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  BaseStatistic,
  componentDestroyed,
  HockeyStatistic,
  Team,
  TournamentTeamUser,
  UntilDestroy
} from '@mtgame/core';
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LeagueRootService } from '@core/services/league-root.service';
import { getLeaguePlayerRoute } from '@config/routes';
import { TournamentTeamService } from '@core/services/tournament-team.service';
import { GROUPED_STATISTIC_SPORT_TYPES } from '@utils/hardcode';
import { getStatisticHeader } from '@utils/statistic';

@UntilDestroy
@Component({
  selector: 'mtg-tournament-team-statistic',
  templateUrl: './tournament-team-statistic.component.html',
  styleUrls: ['./tournament-team-statistic.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TournamentTeamStatisticComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  teamStatistic: BaseStatistic;
  @Input()
  team: Team;
  statisticByMonth: BaseStatistic[];
  winLoseStatistic: BaseStatistic[];
  usersStatistic: BaseStatistic[];
  usersDeclaimedStatistic: BaseStatistic[];
  users: TournamentTeamUser[];
  isBrowser = isPlatformBrowser(this.platformId);
  league = this.leagueRootService.getLeagueModel();
  userRoute = getLeaguePlayerRoute().join('/');
  statisticHeader;

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private tournamentTeamService: TournamentTeamService,
    private cdr: ChangeDetectorRef,
    private leagueRootService: LeagueRootService,
  ) {
  }

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }
    combineLatest([
      this.tournamentTeamService.commonStatistic$.pipe(takeUntil(componentDestroyed(this))),
      this.tournamentTeamService.byMonthStatistic$.pipe(takeUntil(componentDestroyed(this))),
      this.tournamentTeamService.winLoseStatistic$.pipe(takeUntil(componentDestroyed(this)))
    ]).subscribe(([teamStatistic, byMonthStatistic, winLoseStatistic]) => {
      this.teamStatistic = teamStatistic;
      this.statisticByMonth = byMonthStatistic;
      this.winLoseStatistic = winLoseStatistic;
      this.cdr.markForCheck();
    });

    combineLatest([
      this.tournamentTeamService.usersStatistic$.pipe(takeUntil(componentDestroyed(this))),
      this.tournamentTeamService.users$.pipe(takeUntil(componentDestroyed(this)))
    ]).subscribe(([statistic, users]) => {
      const teamUserIds = users.map(item => item.id);
      const userIds = [];
      for (const item of statistic) {
        userIds.push(item.leaguePlayer.id);
      }
      const emptyUsers = users.filter(user => !userIds.includes(user.leaguePlayer.id)).map(user => {
        const stat = new HockeyStatistic();
        stat.leaguePlayer = user.leaguePlayer;
        stat.tournamentTeamUser = user;
        stat.newbie = true;
        return stat;
      });
      this.usersStatistic = [
        ...statistic.filter(item => teamUserIds.includes(item.tournamentTeamUser.id)),
        ...emptyUsers
      ];
      this.usersDeclaimedStatistic = statistic.filter(item => !teamUserIds.includes(item.tournamentTeamUser.id));
      this.users = users;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.team && this.team) {
      this.statisticHeader = getStatisticHeader(this.team.sport);
    }
  }

  ngOnDestroy(): void {
  }

  getStatisticHeader() {
    return this.statisticHeader;
  }

  isGroupedStatistic(): boolean {
    return GROUPED_STATISTIC_SPORT_TYPES.indexOf(this.league.sport.id) > -1;
  }
}
